import React, { useMemo, useState } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import { FilterName } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import {
  FilterDropdownToggle,
  FilterDropdownToggleContent,
} from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.styles'
import {
  DropDownMenuWithNesting,
  FilterOptionContainer,
  FilterItemsContainer,
} from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/FilterDropdown.styles'
import { DropdownItemType } from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/FilterDropdown.constants'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { CountChip } from 'src/components/Reusable/Chips/BasicChip'
import { MenuPlaceholder } from 'src/components/Placeholders/MenuPlaceholder'
import { DropdownSearchInput } from 'src/components/Reusable/Inputs/DropdownSearchInput/DropdownSearchInput'

interface LocalLabelFilterDropdownProps {
  filterName: FilterName
  filterValues: DropdownItemType[]
  isLoading?: boolean
  selectedLabelIds: string[]
  onChangeSelectedLabels: (labelIds: string[]) => void
}

export const LocalLabelFilterDropdown: React.FC<
  LocalLabelFilterDropdownProps
> = ({
  filterName,
  filterValues,
  isLoading,
  selectedLabelIds,
  onChangeSelectedLabels,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const selectedSet = useMemo(
    () => new Set(selectedLabelIds),
    [selectedLabelIds],
  )
  const count = selectedLabelIds.length

  const handleToggle = (nextShow: boolean) => {
    setIsOpen(nextShow)
    if (!nextShow) {
      setSearchQuery('')
    }
  }

  const toggleValue = (val: string) => {
    if (selectedSet.has(val)) {
      onChangeSelectedLabels(selectedLabelIds.filter((id) => id !== val))
    } else {
      onChangeSelectedLabels([...selectedLabelIds, val])
    }
  }

  const hasNested = filterValues.some((fv) => !!fv.nestedFilter)

  const filteredValues = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return filterValues

    return filterValues
      .map((fv) => {
        if (fv.nestedFilter) {
          const matchingChildren = fv.nestedFilter.filterValues.filter(
            (child) =>
              typeof child.label === 'string'
                ? child.label.toLowerCase().includes(query)
                : false,
          )
          const categoryMatches =
            typeof fv.label === 'string' &&
            fv.label.toLowerCase().includes(query)

          if (categoryMatches) {
            return fv
          }

          if (matchingChildren.length > 0) {
            return {
              ...fv,
              nestedFilter: {
                ...fv.nestedFilter,
                filterValues: matchingChildren,
              },
            }
          }

          return null
        }

        const labelStr =
          typeof fv.label === 'string' ? fv.label.toLowerCase() : ''
        return labelStr.includes(query) ? fv : null
      })
      .filter((fv): fv is DropdownItemType => fv !== null)
  }, [filterValues, searchQuery])

  return (
    <div>
      <Dropdown show={isOpen} onToggle={handleToggle} autoClose="outside">
        <Dropdown.Toggle
          as={FilterDropdownToggle}
          type="button"
          id={`${filterName}-local-dropdown`}
          $hasSelection={count > 0}
        >
          <FilterDropdownToggleContent>
            <p>{filterName}</p>
            {count > 0 && <CountChip ml="xxs">{count}</CountChip>}
          </FilterDropdownToggleContent>
        </Dropdown.Toggle>
        {isLoading ? (
          <Dropdown.Menu rootCloseEvent="mousedown">
            <MenuPlaceholder height={108} />
          </Dropdown.Menu>
        ) : (
          <DropDownMenuWithNesting
            $hasNested={hasNested}
            rootCloseEvent="mousedown"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
            style={{
              zIndex: 1055,
              maxWidth: '320px',
              minWidth: '260px',
              width: 'max-content',
            }}
          >
            <DropdownSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search labels..."
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
            <FilterItemsContainer
              style={{
                padding: '8px 12px',
                maxHeight: '280px',
                overflowY: 'auto',
                maxWidth: '320px',
              }}
            >
              {filteredValues.length === 0 ? (
                <p
                  style={{
                    margin: 0,
                    padding: '8px',
                    color: '#6c757d',
                    fontSize: '13px',
                  }}
                >
                  No matching labels found.
                </p>
              ) : (
                filteredValues.map((fv) => {
                  if (fv.nestedFilter) {
                    const groupItems = fv.nestedFilter.filterValues

                    return (
                      <FilterOptionContainer
                        key={fv.value}
                        style={{ marginBottom: '12px' }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: '12px',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            color: '#495057',
                            padding: '4px 0 6px 0',
                          }}
                        >
                          {fv.label}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                          }}
                        >
                          {groupItems.map((subItem) => {
                            const isSubChecked = selectedSet.has(subItem.value)
                            return (
                              <div
                                key={subItem.value}
                                style={{ padding: '2px 0' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <CheckBoxInput
                                  id={subItem.value}
                                  label={subItem.label as React.ReactNode}
                                  checked={isSubChecked}
                                  onChange={() => toggleValue(subItem.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  tabIndex={-1}
                                />
                              </div>
                            )
                          })}
                        </div>
                      </FilterOptionContainer>
                    )
                  }

                  const isChecked = selectedSet.has(fv.value)
                  return (
                    <FilterOptionContainer
                      key={fv.value}
                      style={{ marginBottom: '4px' }}
                    >
                      <div
                        style={{ padding: '2px 0' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <CheckBoxInput
                          id={fv.value}
                          label={fv.label as React.ReactNode}
                          checked={isChecked}
                          onChange={() => toggleValue(fv.value)}
                          onClick={(e) => e.stopPropagation()}
                          tabIndex={-1}
                        />
                      </div>
                    </FilterOptionContainer>
                  )
                })
              )}
            </FilterItemsContainer>
          </DropDownMenuWithNesting>
        )}
      </Dropdown>
    </div>
  )
}
